import React, { useMemo } from 'react';
import {
	ApolloClient,
	ApolloLink,
	ApolloProvider,
	createHttpLink,
	from,
	InMemoryCache,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { setContext } from '@apollo/client/link/context';
import { useMemberExperienceAuth } from '@guildeducationinc/auth-frontend-tools';
import { logDataDogError } from '@src/utilities/datadog';
import {
	CATALOG_SERVICE_URL,
	GRAPHQL_GATEWAY_API_URL,
	GUILD_GLOBAL_API_URL,
	ROLE_PROGRAM_GRAPH_SERVICE_URL,
} from '../../../utilities/envConfig';

export const ROLE_PROGRAM_GRAPH_SERVICE_IDENTIFIER = 'role-program-graph-service';
export const GUILD_GRAPHQL_GATEWAY_IDENTIFIER = 'gateway';
export const GUILD_GLOBAL_APPSYNC_INSTANCE_IDENTIFIER = 'global';
const ACCESS_TOKEN_APIS = [
	ROLE_PROGRAM_GRAPH_SERVICE_IDENTIFIER,
	GUILD_GRAPHQL_GATEWAY_IDENTIFIER,
	GUILD_GLOBAL_APPSYNC_INSTANCE_IDENTIFIER,
];

export const GqlProvider: React.FC = ({ children }) => {
	const { getAccessToken, getIdToken } = useMemberExperienceAuth();

	const client = useMemo(() => {
		const idAuthLink = setContext(async (_, { headers }) => {
			const idToken = await getIdToken();

			return {
				headers: {
					...headers,
					authorization: idToken,
				},
			};
		});

		const guildGlobalLink = createHttpLink({
			fetch,
			uri: GUILD_GLOBAL_API_URL,
		});

		const gatewayLink = createHttpLink({
			fetch,
			uri: GRAPHQL_GATEWAY_API_URL,
		});

		const catalogServiceLink = createHttpLink({
			fetch,
			uri: CATALOG_SERVICE_URL,
		});

		const roleProgramGraphServiceLink = createHttpLink({
			fetch,
			uri: ROLE_PROGRAM_GRAPH_SERVICE_URL,
		});

		const requestLink = ApolloLink.split(
			operation => operation.getContext().gqlServer === GUILD_GLOBAL_APPSYNC_INSTANCE_IDENTIFIER,
			guildGlobalLink,
			ApolloLink.split(
				operation => operation.getContext().gqlServer === ROLE_PROGRAM_GRAPH_SERVICE_IDENTIFIER,
				roleProgramGraphServiceLink,
				ApolloLink.split(
					operation => operation.getContext().gqlServer === GUILD_GRAPHQL_GATEWAY_IDENTIFIER,
					gatewayLink,
					catalogServiceLink
				)
			)
		);

		const retryLink = new RetryLink({
			delay: {
				initial: 500,
				max: 30000,
				jitter: true,
			},
			attempts: {
				max: 2,
				retryIf: (error, operation) => {
					return (
						operation.getContext().gqlServer === ROLE_PROGRAM_GRAPH_SERVICE_IDENTIFIER &&
						!!error &&
						error.message?.includes('Connection has been closed')
					);
				},
			},
		});

		const accessAuthLink = setContext(async (_, { headers }) => {
			const accessToken = await getAccessToken();

			return {
				headers: {
					...headers,
					authorization: `Bearer ${accessToken}`,
				},
			};
		});

		const authLink = ApolloLink.split(
			operation => ACCESS_TOKEN_APIS.includes(operation.getContext().gqlServer),
			accessAuthLink,
			idAuthLink
		);

		const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
			if (graphQLErrors) {
				graphQLErrors.forEach(error => {
					logDataDogError(error, 'GraphQLError', {
						error,
						catalogContext: operation.operationName,
					});
				});
			}
			if (networkError && Object.keys(networkError).length > 0) {
				logDataDogError(networkError, 'GraphQLNetworkError', {
					catalogContext: operation.operationName,
				});
			}
		});
		return new ApolloClient({
			link: from([authLink, retryLink, errorLink, requestLink]),
			cache: new InMemoryCache(),
		});
	}, [getIdToken, getAccessToken]);

	return <ApolloProvider client={client}>{children}</ApolloProvider>;
};
